/**
 * Custom hook for managing submissions state
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { submissionsApi } from '../../api/modules/submissions';
import type { FormSubmission } from '../../types/submissions';
import type { SubmissionsState, DateRangeDays } from './types';

const DEFAULT_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Convert days to startDate/endDate for API
 */
function getDateRangeParams(days: DateRangeDays): { startDate?: string; endDate?: string } {
  if (days === null) {
    return {}; // All time - no date filter
  }

  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
}

export const useSubmissionsState = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [state, setState] = useState<SubmissionsState>({
    submissions: [],
    stats: null,
    isLoading: true,
    error: null,
    selectedSubmission: null,
    pagination: {
      page: 1,
      limit: DEFAULT_LIMIT,
      total: 0,
      totalPages: 0,
    },
    filters: {
      includeArchived: false,
      unreadOnly: false,
      dateRangeDays: null, // Default: all time
      search: '',
    },
  });

  // Load submissions
  const loadSubmissions = useCallback(
    async (page = 1) => {
      if (!siteId) return;

      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const dateParams = getDateRangeParams(state.filters.dateRangeDays);
        const [submissionsResponse, statsResponse] = await Promise.all([
          submissionsApi.listSubmissions(siteId, {
            page,
            limit: state.pagination.limit,
            includeArchived: state.filters.includeArchived,
            unreadOnly: state.filters.unreadOnly,
            search: state.filters.search || undefined,
            ...dateParams,
          }),
          submissionsApi.getStats(siteId),
        ]);

        setState(prev => ({
          ...prev,
          submissions: submissionsResponse.submissions,
          stats: statsResponse,
          pagination: submissionsResponse.pagination,
          isLoading: false,
        }));
      } catch (error) {
        console.error('Failed to load submissions:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load submissions. Please try again.',
        }));
      }
    },
    [
      siteId,
      state.pagination.limit,
      state.filters.includeArchived,
      state.filters.unreadOnly,
      state.filters.dateRangeDays,
      state.filters.search,
    ]
  );

  // Initial load
  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  // Page change handler
  const handlePageChange = useCallback(
    (newPage: number) => {
      loadSubmissions(newPage);
    },
    [loadSubmissions]
  );

  // Filter change handlers
  const setIncludeArchived = useCallback((value: boolean) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, includeArchived: value },
      pagination: { ...prev.pagination, page: 1 },
    }));
  }, []);

  const setUnreadOnly = useCallback((value: boolean) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, unreadOnly: value },
      pagination: { ...prev.pagination, page: 1 },
    }));
  }, []);

  const setDateRangeDays = useCallback((value: DateRangeDays) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, dateRangeDays: value },
      pagination: { ...prev.pagination, page: 1 },
    }));
  }, []);

  // Debounced search setter
  const setSearch = useCallback((value: string) => {
    // Clear any pending debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Update the search value immediately for UI responsiveness
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, search: value },
    }));

    // Debounce the actual search (resets page to 1)
    searchDebounceRef.current = setTimeout(() => {
      setState(prev => ({
        ...prev,
        pagination: { ...prev.pagination, page: 1 },
      }));
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Selection handler
  const setSelectedSubmission = useCallback((submission: FormSubmission | null) => {
    setState(prev => ({ ...prev, selectedSubmission: submission }));
  }, []);

  // Update submission in list (for optimistic updates)
  const updateSubmissionInList = useCallback((updated: FormSubmission) => {
    setState(prev => ({
      ...prev,
      submissions: prev.submissions.map(s => (s.id === updated.id ? updated : s)),
      selectedSubmission:
        prev.selectedSubmission?.id === updated.id ? updated : prev.selectedSubmission,
    }));
  }, []);

  // Remove submission from list
  const removeSubmissionFromList = useCallback((submissionId: string) => {
    setState(prev => ({
      ...prev,
      submissions: prev.submissions.filter(s => s.id !== submissionId),
      selectedSubmission:
        prev.selectedSubmission?.id === submissionId ? null : prev.selectedSubmission,
      pagination: {
        ...prev.pagination,
        total: Math.max(0, prev.pagination.total - 1),
      },
    }));
  }, []);

  return {
    siteId,
    ...state,
    loadSubmissions,
    handlePageChange,
    setIncludeArchived,
    setUnreadOnly,
    setDateRangeDays,
    setSearch,
    setSelectedSubmission,
    updateSubmissionInList,
    removeSubmissionFromList,
  };
};
